import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';

/**
 * Support page — required for App Store submissions and useful for
 * players. Keeps the tone friendly, palette-matched, and covers the
 * five real questions that hit us in feedback: subscription billing,
 * lost streaks, wrong-cell hints, hint sync, and account deletion.
 */
export default function SupportPage() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-main)' }}>
      <Navbar />
      <main
        className="max-w-3xl mx-auto px-4 py-12 text-[var(--text-primary)]"
        data-testid="support-page"
      >
        <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-2 text-[#3B2416]">
          Support
        </h1>
        <p className="text-base text-[var(--text-secondary)] mb-10">
          We answer every email within two business days. If you're stuck
          on a puzzle, try the checklist below first — it fixes 90% of
          reports before they hit our inbox.
        </p>

        <Section title="Get in touch">
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <strong>General questions:</strong>{' '}
              <a className="underline text-[#8B5A3C]" href="mailto:hello@sudokuacademy.org">
                hello@sudokuacademy.org
              </a>
            </li>
            <li>
              <strong>Billing &amp; subscriptions:</strong>{' '}
              <a className="underline text-[#8B5A3C]" href="mailto:billing@sudokuacademy.org">
                billing@sudokuacademy.org
              </a>
            </li>
            <li>
              <strong>Privacy / data requests:</strong>{' '}
              <a className="underline text-[#8B5A3C]" href="mailto:privacy@sudokuacademy.org">
                privacy@sudokuacademy.org
              </a>
            </li>
            <li>
              <strong>Bug report:</strong> use the "Report puzzle" thumbs-down
              on any puzzle page, or email us at{' '}
              <a className="underline text-[#8B5A3C]" href="mailto:bugs@sudokuacademy.org">
                bugs@sudokuacademy.org
              </a>
            </li>
          </ul>
        </Section>

        <Section title="Frequently asked">
          <FAQ q="Why is a hint telling me to remove a candidate I already erased?">
            We recently shipped a fix: the hint solver now respects the
            pencil marks you've already narrowed. If you're still seeing
            this, make sure you're on the latest version (hard-refresh:
            Cmd/Ctrl + Shift + R). If it persists, please report the
            puzzle via the thumbs-down so we can inspect it.
          </FAQ>

          <FAQ q="I entered a wrong digit and the hint is confusing.">
            Run <strong>Check My Work</strong> from the numpad — wrong
            cells turn red. Tap "Clear Wrong" (one undo restores
            everything) or fix them manually, then retry the hint.
          </FAQ>

          <FAQ q="How do I cancel my Premium subscription?">
            Go to <Link to="/subscription" className="underline text-[#8B5A3C]">Subscription</Link>{' '}
            and click "Manage in Stripe". You can cancel anytime; access
            continues through your paid period. We do not offer prorated
            refunds for partial months.
          </FAQ>

          <FAQ q="I lost my streak — can you restore it?">
            Streaks reset if you miss a calendar day (UTC). We generally
            don't restore streaks manually, but if the loss was due to a
            server outage on our end, email
            {' '}<a className="underline text-[#8B5A3C]" href="mailto:hello@sudokuacademy.org">hello@sudokuacademy.org</a>{' '}
            with your username and we'll check the logs.
          </FAQ>

          <FAQ q="I forgot my password.">
            Use the <Link to="/forgot-password" className="underline text-[#8B5A3C]">Forgot Password</Link>{' '}
            page. If you registered with a username only (no email), sign
            in with Google or Apple instead, or email us to help recover
            the account.
          </FAQ>

          <FAQ q="How do I delete my account?">
            Sign in, open <Link to="/dashboard" className="underline text-[#8B5A3C]">Dashboard</Link>,
            scroll to the "Danger Zone" section at the bottom, and follow
            the type-DELETE confirmation. Deletion is immediate and
            permanent — we cannot recover the account afterward.
          </FAQ>

          <FAQ q="This puzzle is unsolvable / has multiple solutions.">
            Every algorithmic puzzle on Sudoku Academy is guaranteed
            solvable by pure logic (no guessing). If you find a
            counterexample, please tap the thumbs-down on the puzzle
            page — that flags it for review and takes it out of rotation
            while we inspect it.
          </FAQ>

          <FAQ q="I'm on the App Store version and Premium isn't unlocking.">
            The mobile app forwards subscription state from the web. Make
            sure you're signed in with the same account you used to
            subscribe. If Premium still doesn't appear after 60 seconds,
            pull-to-refresh, then contact billing support.
          </FAQ>
        </Section>

        <Section title="Community">
          <p>
            Join our Discord for daily-challenge discussions, technique
            walkthroughs, and puzzle debates:{' '}
            <a className="underline text-[#8B5A3C]" href="https://discord.gg/sudokuacademy" target="_blank" rel="noreferrer">
              discord.gg/sudokuacademy
            </a>
          </p>
        </Section>

        <div className="mt-16 text-sm text-[var(--text-muted)]">
          <Link to="/privacy" className="underline hover:text-[#8B5A3C]" data-testid="support-to-privacy-link">
            Read our privacy policy →
          </Link>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="text-2xl font-heading font-semibold mb-3 text-[#3B2416]">
        {title}
      </h2>
      <div className="space-y-3 text-[var(--text-secondary)] leading-relaxed">
        {children}
      </div>
    </section>
  );
}

function FAQ({ q, children }) {
  return (
    <details className="border border-[var(--border-subtle)] rounded-lg px-4 py-3 bg-[var(--background-surface)]">
      <summary className="cursor-pointer font-semibold text-[#3B2416]">
        {q}
      </summary>
      <div className="mt-2 pl-1 text-[var(--text-secondary)]">
        {children}
      </div>
    </details>
  );
}
